<template>
  <div class="col-12 col track-slider" :class="$config.project">
    <div
      class="heading" :to="`${track.slug}/`"
      :data-event="`${track.slug.replace(/-/g, '_')}_track.click`"
    >
      <h3 class="title">
        {{ track.name }}
        <span v-show="badgeText" class="badge badge-lg" :class="`badge-${badgeText.toLowerCase()}`">
          {{ badgeText }}
        </span>
      </h3>
      <nuxt-link
        v-show="$store.state.layout === 'desktop' || $store.state.tablet"
        class="btn-primary view-details"
        :to="`${track.slug}/`"
        :data-event="`${track.slug.replace(/-/g, '_')}_track.click`"
      >
        {{ $config.platform === 'ds' ? 'Get Started' : 'View Details' }}
      </nuxt-link>
    </div>
    <div class="description" v-html="track.longDescription" />
    <Slider :disabled="track.courses && track.courses.length <= 3">
      <div slot="heading" class="holder" :class="{ 'enrolled': track.enrolled }">
        <template v-if="$config.project === 'lms' && track.enrolled">
          <Progress
            :progress="track.progress"
            :data="{ completed: track.completedCourses, total: track.courses.length }"
            text="courses"
            offset
            :certificate="!!track.certificateNumber"
            class="info"
          />
          <Medal v-show="track.completed" class="completed" />
        </template>
        <template v-else>
          <div class="courses">
            <Monitor />
            <span class="text"> {{ track.courses.length }} Courses</span>
          </div>
          <div class="certificate">
            <Medal />
            <span class="text">Certificate of Achievement</span>
          </div>
        </template>
      </div>
      <CourseCard
        v-for="course in track.courses"
        :key="course.id"
        slot="slide"
        :course="course"
        class="col col-4"
      />
    </Slider>
    <nuxt-link
      v-show="$store.state.layout === 'mobile' && !$store.state.tablet"
      class="btn-primary view-details"
      :to="`${track.slug}/`"
      :data-event="`${track.slug.replace(/-/g, '_')}_track.click`"
    >
      {{ $config.platform === 'ds' ? 'Get Started' : 'View Details' }}
    </nuxt-link>
  </div>
</template>

<script>
import Slider from '../components/Slider';
import CourseCard from '../Courses/Card';
import Progress from '../Courses/Progress';
import Medal from '../assets/svg/medal.svg?vue-component';
import Monitor from '../assets/svg/courses/monitor.svg?vue-component';

export default {
  components: {
    Monitor,
    Medal,
    Slider,
    CourseCard,
    Progress,
  },
  props: {
    track: { type: Object, default: () => ({}), required: true },
  },
  computed: {
    badgeText() {
      if (this.track.certificateNumber) {
        return 'Completed';
      }
      if (!this.track.completed && this.track.enrolled) {
        return 'Enrolled';
      }
      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
.track-slider {
  padding: var(--spacing-80) 87px;
  background-color: var(--main-light-shade-2);
  border-radius: 15px;
  margin: 0 0 var(--spacing-40) 0;

  .heading {
    color: var(--main-dark-shade-0);
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-24);

    .title {
      display: inline-flex;
      justify-content: center;
      column-gap: 8px;
      line-height: 110%;
      margin: 0;

      .badge {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        width: 92px;
        height: 28px;
        font-size: 14px;
        font-weight: 500;
        border-radius: 4px;

        &.badge-completed {
          background-color: rgba(70, 208, 100, 0.2);
          color: var(--main-green);
        }

        &.badge-enrolled {
          background-color: rgba(rgb(86, 191, 199), 0.2);
          color: var(--accent);
        }
      }
    }

    .view-details {
      width: 200px;
      height: 44px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: 600;
    }
  }

  .description {
    color: var(--main-dark-light-4);
    line-height: 150%;
    margin-top: 0;
    margin-bottom: var(--spacing-30);
  }

  ::v-deep .slider-container {
    .slider-header {
      margin-bottom: var(--spacing-14);

      .holder {
        display: flex;
        column-gap: 24px;

        &.enrolled {
          column-gap: 12px;
        }

        .certificate,
        .courses {
          display: flex;
          align-items: center;
          column-gap: 8px;
          margin: 0;

          .text {
            line-height: 167%;
          }

          svg {
            width: 22px;
            height: 22px;
            margin-right: 0;

            path[stroke] {
              stroke: var(--main-dark);
            }
          }
        }

        .course-progress {
          column-gap: 44px;
          gap: var(--spacing-8);

          .wrapper {
            margin: 0;
          }

          .progress-bar {
            border: 1px solid var(--main-light-shade-3);
            width: 328px;
            margin: 0;
          }
        }

        .completed {
          path[stroke] {
            stroke: var(--accent);
          }
        }
      }

      .controls {
        svg {
          &:not(.disabled) {
            circle {
              fill: transparent;
              stroke: var(--main-dark-shade-2);
            }
          }
        }
      }
    }

    .swiper {
      padding: var(--spacing-20) 0;

      .course-card {
        margin-bottom: 0;
      }
    }
  }
}

@include bp($bp-laptop) {
  .track-slider {
    padding: var(--spacing-60) var(--spacing-55);
  }
}

@include bp($bp-mobile) {
  .track-slider {
    padding: 0;
    background-color: unset;
    border-radius: 10;
    margin: 0;
    margin-top: 62px;
    margin-bottom: 62px;

    &:last-child {
      margin-bottom: 0;
    }

    ::v-deep .slider-container {
      .slider-header {
        .holder {
          flex-direction: column;

          .courses {
            margin-left: 0;
            margin-right: 0;
          }

          &.enrolled {
            flex-direction: row;
          }
        }
      }

      .swiper {
        .course-card {
          max-width: 50%;
          width: 50%;
          flex: 0 0 50%;
        }
      }
    }

    @media screen and (max-width: 750px) {
      .heading {
        flex-direction: column;

        h3 {
          align-self: flex-start;
        }

        a {
          align-self: flex-start;
          margin-top: var(--spacing-24);
        }
      }
    }
  }

  .title {
    font-size: 24px;

    &:nth-of-type(2) {
      margin-top: 50px;
    }
  }

  .description {
    width: unset !important;
  }

  ::v-deep .slider-container {
    .dots {
      .dot {
        &:after {
          background-color: var(--main-light-shade-3);
        }

        &.active {
          &:after {
            background-color: var(--main-light-shade-4);
          }
        }
      }
    }

    &.featured-courses {
      .dots {
        background-color: var(--main-dark);
        padding-bottom: var(--spacing-80);

        .dot {
          &:after {
            background-color: rgba(255, 255, 255, 0.48);
          }

          &.active {
            &:after {
              background-color: var(--main-light);
            }
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .track-slider {
    margin-top: 0;

    &.lms {
      margin-bottom: var(--spacing-80);
    }

    .heading {
      margin-bottom: var(--spacing-16);
      flex-direction: column;

      .title {
        align-self: flex-start;
        display: flex;
        flex-direction: column-reverse;
        row-gap: 6px;
      }
    }

    .description {
      line-height: 150%;
      margin-bottom: var(--spacing-32);
    }

    ::v-deep .slider-container {
      .slider-header {
        .heading {
          .holder {
            &.enrolled {
              column-gap: 6px;
            }

            .courses {
              margin-bottom: var(--spacing-12);
            }

            .course-progress {
              column-gap: 16px;

              .progress-bar {
                width: 68px;
              }
            }
          }
        }
      }

      .swiper {
        .course-card {
          max-width: 100%;
          width: 100%;
          flex: 0 0 100%;

          ::v-deep .course-card-container {
            .course-card-body {
              padding: var(--spacing-12) var(--spacing-16);

              a {
                .instructors {
                  font-weight: 500;
                }

                .course-rating {
                  margin-bottom: var(--spacing-12);
                }
              }
            }
          }
        }
      }

      .dots {
        padding: var(--spacing-20) 0 var(--spacing-40) 0;
      }
    }

    .view-details {
      width: 100%;
    }
  }
}

.fa {
  .track-slider {
    .heading .title .badge.badge-enrolled {
      background: rgba(3, 111, 253, 0.2);
    }

    ::v-deep .slider-container {
      .slider-header .controls svg:not(.disabled) {
        circle[stroke],
        path[stroke] {
          stroke: var(--accent-shade-3);
        }
      }

      .swiper .course-card .course-card-container {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        border-radius: 10px;
      }
    }
  }
}
</style>
